import { useEffect, useId, useRef, useState } from "react";
import type { LeadCardResponse } from "@gioia365/contracts";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { Dialog } from "../../design/components/Dialog";
import { Icon } from "../../design/components/Icon";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { formatNumericDateTime } from "../../i18n/format";
import { useLocale, useT } from "../../i18n/locale";
import { useAccess } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { EMAIL_SHAPE, formatPhoneForDisplay, roughE164 } from "../lead-display";
import { isCardLevelError, type CardLead, type LeadCardWrites } from "../lead-queries";
import {
  cityEdit,
  cityLabel,
  cleanText,
  customEdit,
  formatEditableNumber,
  formatEuro,
  formatNumber,
  leadCountryName,
  matchOf,
  parseDecimal,
  phoneEdit,
  phoneOf,
  SECTION_TITLES,
  secondHolderEdit,
  secondHolderRemoval,
  SYSTEM_FIELD_LABELS,
  TEXT_LIMITS,
  textEdit,
  type CardField,
  type CardSection,
  type PhoneSlot,
} from "./card-fields";
import type { CityValue } from "./CityPicker";
import { EditableField, type Commit, type EditorValue } from "./FieldEditor";
import { OwnerDialog } from "./OwnerDialog";

// I dati della scheda (piano M2 Task 18, mock-up `scheda-lead.html`, «Dati del Lead»): le sezioni e i campi come li
// dispone il modello pubblicato in «Campi della scheda», ognuna richiudibile, con righe etichetta · valore. Un campo
// nascosto dal modello non c'è; i telefoni ci sono sempre (lo garantisce il server). L'owner si cambia in una finestra
// con il motivo; il secondo titolare si aggiunge e si toglie, con una conferma; provincia, regione e nazione sono righe
// del modello come le altre (dal 1° ottobre 2026 in «Contatto e responsabilità», dopo l'email), ma non si scrivono:
// vengono dalla città, con «dalla città», e cambiano quando cambia la città. Sotto il cellulare, «Non inviare WhatsApp» (piano M6,
// R1, Task 4b; `WhatsAppOptOutRow`).

type Props = {
  tab: TabContext;
  card: LeadCardResponse;
  writes: LeadCardWrites;
  onCardError: (error: unknown) => void;
};

const SAME: Commit = { kind: "same" };

/** Un `id` del DOM dal riferimento del campo (`system:first_name` → `lead-system-first-name`). */
function fieldId(ref: string): string {
  return `lead-${ref.replace(/[^a-z0-9]+/gi, "-")}`;
}

export function LeadDataBlock({ tab, card, writes, onCardError }: Props) {
  const t = useT();
  const editableAny = card.model.sections.some((section) => section.fields.some((field) => field.editable && field.ref !== "system:owner"));
  return (
    <aside className="g-lead-data" aria-label={t("leadCard.data.title")}>
      {editableAny ? <p className="g-lead-data__help">{t("leadCard.data.help")}</p> : null}
      {card.model.sections.map((section) => (
        <Section key={section.key} section={section} lead={card.lead} whatsapp={card.whatsapp} tab={tab} writes={writes} onCardError={onCardError} />
      ))}
    </aside>
  );
}

type RowContext = { lead: CardLead; whatsapp: LeadCardResponse["whatsapp"]; tab: TabContext; writes: LeadCardWrites; onCardError: (error: unknown) => void };

function Section({ section, ...context }: RowContext & { section: CardSection }) {
  const t = useT();
  return (
    <details open className="g-lead-data__section">
      <summary>{t(SECTION_TITLES[section.key])}</summary>
      {section.key === "salon" ? <p className="g-lead-data__note">{t("leadCard.data.salonNote")}</p> : null}
      <dl>
        {section.fields.map((field) => (
          <FieldRows key={field.ref} field={field} {...context} />
        ))}
      </dl>
    </details>
  );
}

function FieldRows({ field, ...context }: RowContext & { field: CardField }) {
  switch (field.ref) {
    case "system:first_name":
      return <TextRow field={field} column="firstName" maxLength={TEXT_LIMITS.name} {...context} />;
    case "system:last_name":
      return <TextRow field={field} column="lastName" maxLength={TEXT_LIMITS.name} {...context} />;
    case "system:salon_name":
      return <TextRow field={field} column="salonName" maxLength={TEXT_LIMITS.salonName} {...context} />;
    case "system:email":
      return <EmailRow field={field} {...context} />;
    case "system:mobile":
      return (
        <>
          <PhoneRow field={field} slot={{ holder: "primary", kind: "mobile" }} labelKey="leadCard.fields.mobile" {...context} />
          <WhatsAppOptOutRow whatsapp={context.whatsapp} writes={context.writes} onCardError={context.onCardError} />
        </>
      );
    case "system:landline":
      return <PhoneRow field={field} slot={{ holder: "primary", kind: "landline" }} labelKey="leadCard.fields.landline" {...context} />;
    case "system:owner":
      return <OwnerRow field={field} {...context} />;
    case "system:second_holder":
      return <SecondHolderRows field={field} {...context} />;
    case "system:city":
      return <CityRow field={field} {...context} />;
    case "system:province":
    case "system:region":
    case "system:country":
      return <PlaceRow field={field} lead={context.lead} />;
    default:
      return field.ref.startsWith("field:") ? <CustomRow field={field} {...context} /> : null;
  }
}

/** Le prop comuni di ogni riga che si modifica. */
function common(field: CardField, context: RowContext) {
  return { editable: field.editable, tab: context.tab, onSave: context.writes.save, onCardError: context.onCardError };
}

function TextRow({ field, column, maxLength, ...context }: RowContext & { field: CardField; column: "firstName" | "lastName" | "salonName"; maxLength: number }) {
  const t = useT();
  const labelKey = SYSTEM_FIELD_LABELS[field.ref];
  const value = context.lead[column];
  return (
    <EditableField
      id={fieldId(field.ref)}
      label={labelKey ? t(labelKey) : field.ref}
      display={value}
      spec={{ kind: "text", maxLength }}
      initial={value ?? ""}
      commit={(raw) => {
        const next = cleanText(String(raw));
        return next === cleanText(value ?? "") ? SAME : { kind: "save", edit: textEdit(column, next) };
      }}
      {...common(field, context)}
    />
  );
}

/** Il commit di un'email: vuota la toglie; una forma sbagliata resta nella casella con il motivo. */
function emailCommit(raw: EditorValue, current: string | null, message: string, save: (value: string | null) => Commit): Commit {
  const next = String(raw).trim() || null;
  if (next === current) return SAME;
  if (next !== null && !EMAIL_SHAPE.test(next)) return { kind: "invalid", message };
  return save(next);
}

function EmailRow({ field, ...context }: RowContext & { field: CardField }) {
  const t = useT();
  const value = context.lead.email;
  return (
    <EditableField
      id={fieldId(field.ref)}
      label={t("leadCard.fields.email")}
      display={value}
      spec={{ kind: "email" }}
      initial={value ?? ""}
      commit={(raw) => emailCommit(raw, value, t("leadCard.errors.email"), (next) => ({ kind: "save", edit: textEdit("email", next) }))}
      {...common(field, context)}
    />
  );
}

/** Il commit di un telefono: vuoto lo toglie; lo stesso numero scritto in un altro modo non cambia nulla. */
function phoneCommit(raw: EditorValue, slot: PhoneSlot, current: string | null): Commit {
  const composed = String(raw).trim();
  if ((composed ? roughE164(composed) : null) === current) return SAME;
  return { kind: "save", edit: phoneEdit(slot, composed || null) };
}

function PhoneRow({ field, slot, labelKey, ...context }: RowContext & { field: CardField; slot: PhoneSlot; labelKey: "leadCard.fields.mobile" | "leadCard.fields.landline" }) {
  const t = useT();
  const value = phoneOf(context.lead, slot);
  return (
    <EditableField
      id={fieldId(field.ref)}
      label={t(labelKey)}
      display={value ? formatPhoneForDisplay(value) : null}
      spec={{ kind: "phone" }}
      initial={value ?? ""}
      commit={(raw) => phoneCommit(raw, slot, value)}
      {...common(field, context)}
    />
  );
}

/**
 * «Non inviare WhatsApp» sotto il cellulare (piano M6, R1, Task 4b; D25, questione 392; mock-up `scheda-lead.html` e `mockup.js`,
 * pacchetto #115): di partenza spento, ogni Lead riceve i WhatsApp. Lo accende chi lavora il Lead (`canSwitchOptOutOn` della scheda),
 * lo spengono soltanto Super user, Admin e Super admin (`canSwitchOptOutOff`, questione 392): spento per chi guarda, la riga dice
 * sempre perché (chi lo accende, chi lo spegne). Il motivo di «WhatsApp» spento della scheda non serve qui: dice prima il blocco
 * del Lead (status, cellulare), non chi può toccare l'interruttore. Acceso dice da chi e quando, e «WhatsApp» e «Invia WhatsApp» si spengono con il motivo (il WhatsApp della risposta
 * va subito nella scheda); le chiamate restano. La scrittura porta la versione del Lead, in coda con le altre della scheda: un
 * rifiuto della scheda intera la rilegge e lo dice in cima. Una scheda che non dice niente del WhatsApp (di prima del Task 3b) non
 * ha la riga.
 */
function WhatsAppOptOutRow({ whatsapp, writes, onCardError }: Pick<RowContext, "whatsapp" | "writes" | "onCardError">) {
  const t = useT();
  const toast = useToast();
  const access = useAccess();
  const id = useId();
  // Il valore che si sta scrivendo, finché il server non risponde: l'interruttore lo mostra e intanto non si tocca.
  const [switching, setSwitching] = useState<boolean | null>(null);
  if (!whatsapp.available && whatsapp.reason === null) return null;
  const { optOut } = whatsapp;
  const on = optOut !== null;
  const allowed = on ? whatsapp.canSwitchOptOutOff : whatsapp.canSwitchOptOutOn;
  const when = optOut ? formatNumericDateTime(access.context.settings.timeZone, optOut.at) : null;
  const since =
    optOut === null || when === null
      ? null
      : optOut.byDisplayName
        ? t("whatsapp.optOut.sinceBy", { name: optOut.byDisplayName, when })
        : t("whatsapp.optOut.since", { when });
  const why = on ? (whatsapp.canSwitchOptOutOff ? null : t("whatsapp.optOut.onlyManagers")) : whatsapp.canSwitchOptOutOn ? null : t("whatsapp.optOut.onlyEditors");
  const hint = [since, why].filter((part) => part !== null).join(" ");

  function change(next: boolean) {
    if (switching !== null || next === on || !allowed) return;
    setSwitching(next);
    writes
      .setWhatsAppOptOut(next)
      .then(
        () => toast.show(t(next ? "whatsapp.optOut.switchedOn" : "whatsapp.optOut.switchedOff"), "success"),
        (error: unknown) => {
          if (isCardLevelError(error)) onCardError(error);
          else toast.show(messageForError(t, error), "warning");
        },
      )
      .finally(() => setSwitching(null));
  }

  return (
    <div className="g-lead-data__row">
      <dt>{t("whatsapp.optOut.row")}</dt>
      <dd>
        <Checkbox
          id={`${id}-opt-out`}
          className="g-lead-data__check"
          label={t("whatsapp.optOut.label")}
          checked={switching ?? on}
          // Durante la scrittura resta abilitato (il focus non si perde), ma un altro clic non parte: `change` lo ignora.
          disabled={!allowed}
          aria-busy={switching === null ? undefined : true}
          hint={hint === "" ? undefined : hint}
          onChange={change}
        />
      </dd>
    </div>
  );
}

function OwnerRow({ field, lead, tab, writes }: RowContext & { field: CardField }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const label = t("leadCard.fields.owner");
  const shown = lead.owner?.displayName ?? t("leadCard.noOwner");
  const sub = lead.owner && !lead.owner.active ? <span className="g-lead-data__sub g-lead-data__warning">{t("leadCard.ownerInactive")}</span> : null;
  return (
    <div className="g-lead-data__row">
      <dt>{label}</dt>
      <dd>
        {field.editable ? (
          <button
            type="button"
            className="g-lead-data__value"
            data-empty={lead.owner ? undefined : "true"}
            aria-haspopup="dialog"
            aria-label={t("leadCard.data.editLabel", { field: label, value: shown })}
            onClick={() => setOpen(true)}
          >
            {shown}
          </button>
        ) : (
          <span className="g-lead-data__text" data-empty={lead.owner ? undefined : "true"}>{shown}</span>
        )}
        {sub}
        {open ? <OwnerDialog tab={tab} lead={lead} onSubmit={writes.changeOwner} onClose={() => setOpen(false)} /> : null}
      </dd>
    </div>
  );
}

/**
 * Il secondo titolare: nome, cognome, cellulare (soltanto il cellulare, decisione confermata) ed email. Senza, c'è
 * «Aggiungi un altro titolare», che fa comparire le righe e apre subito il nome; «Togli il secondo titolare» chiede
 * conferma e toglie i quattro dati insieme.
 */
function SecondHolderRows({ field, ...context }: RowContext & { field: CardField }) {
  const t = useT();
  const toast = useToast();
  const { lead } = context;
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const addRef = useRef<HTMLButtonElement>(null);
  const focusAdd = useRef(false);
  const shown = lead.secondHolder !== null || adding;

  // Tolto il secondo titolare, il focus va su «Aggiungi un altro titolare», come nel mock-up.
  useEffect(() => {
    if (shown || !focusAdd.current) return;
    focusAdd.current = false;
    addRef.current?.focus();
  }, [shown]);

  const holder = lead.secondHolder;
  const label = t("leadCard.fields.secondHolder");
  if (!shown) {
    if (!field.editable) return null;
    return (
      <div className="g-lead-data__row g-lead-data__row--action">
        <dt className="g-visually-hidden">{label}</dt>
        <dd>
          <Button ref={addRef} variant="ghost" onClick={() => setAdding(true)}>
            <Icon name="plus" />
            {t("leadCard.secondHolder.add")}
          </Button>
        </dd>
      </div>
    );
  }

  function remove() {
    if (holder === null) {
      // Le righe aggiunte e mai salvate spariscono senza chiedere nulla al server.
      focusAdd.current = true;
      setAdding(false);
      return;
    }
    setConfirming(true);
  }

  function confirmRemoval() {
    setConfirming(false);
    focusAdd.current = true;
    setAdding(false);
    context.writes.save(secondHolderRemoval()).then(
      () => toast.show(t("leadCard.secondHolder.removed"), "success"),
      (error: unknown) => {
        if (isCardLevelError(error)) context.onCardError(error);
        else toast.show(messageForError(t, error), "warning");
      },
    );
  }

  const nameRow = (part: "firstName" | "lastName", labelKey: "leadCard.fields.secondHolderFirstName" | "leadCard.fields.secondHolderLastName", startEditing: boolean) => {
    const value = holder?.[part] ?? null;
    return (
      <EditableField
        key={part}
        id={`lead-second-${part}`}
        label={t(labelKey)}
        display={value}
        spec={{ kind: "text", maxLength: TEXT_LIMITS.name }}
        initial={value ?? ""}
        startEditing={startEditing}
        commit={(raw) => {
          const next = cleanText(String(raw));
          return next === cleanText(value ?? "") ? SAME : { kind: "save", edit: secondHolderEdit(lead, part, next) };
        }}
        {...common(field, context)}
      />
    );
  };
  const mobile = holder?.mobile ?? null;
  const email = holder?.email ?? null;

  return (
    <>
      {nameRow("firstName", "leadCard.fields.secondHolderFirstName", adding && holder === null)}
      {nameRow("lastName", "leadCard.fields.secondHolderLastName", false)}
      <EditableField
        id="lead-second-mobile"
        label={t("leadCard.fields.secondHolderMobile")}
        display={mobile ? formatPhoneForDisplay(mobile) : null}
        spec={{ kind: "phone" }}
        initial={mobile ?? ""}
        commit={(raw) => phoneCommit(raw, { holder: "second", kind: "mobile" }, mobile)}
        {...common(field, context)}
      />
      <EditableField
        id="lead-second-email"
        label={t("leadCard.fields.secondHolderEmail")}
        display={email}
        spec={{ kind: "email" }}
        initial={email ?? ""}
        commit={(raw) => emailCommit(raw, email, t("leadCard.errors.email"), (next) => ({ kind: "save", edit: secondHolderEdit(lead, "email", next) }))}
        {...common(field, context)}
      />
      {field.editable ? (
        <div className="g-lead-data__row g-lead-data__row--action">
          <dt className="g-visually-hidden">{label}</dt>
          <dd>
            <Button variant="ghost" aria-haspopup={holder === null ? undefined : "dialog"} onClick={remove}>
              <Icon name="minus" />
              {t("leadCard.secondHolder.remove")}
            </Button>
          </dd>
        </div>
      ) : null}
      {confirming ? (
        <Dialog
          open
          title={t("leadCard.secondHolder.confirmTitle")}
          onClose={() => setConfirming(false)}
          footer={
            <>
              <Button onClick={() => setConfirming(false)}>{t("common.cancel")}</Button>
              <Button variant="primary" onClick={confirmRemoval}>
                {t("leadCard.secondHolder.confirm")}
              </Button>
            </>
          }
        >
          <p>{t("leadCard.secondHolder.confirmText")}</p>
        </Dialog>
      ) : null}
    </>
  );
}

/** La città, che si sceglie dal selettore: provincia, regione e nazione ne vengono (`PlaceRow`). */
function CityRow({ field, ...context }: RowContext & { field: CardField }) {
  const t = useT();
  const { place } = context.lead;
  const initial: CityValue = place ? { text: cityLabel(place), match: matchOf(place) } : { text: "", match: null };
  return (
    <EditableField
      id={fieldId(field.ref)}
      label={t("leadCard.fields.city")}
      display={place ? cityLabel(place) : null}
      spec={{ kind: "city" }}
      initial={initial}
      commit={(raw) => {
        const value = typeof raw === "string" ? { text: raw, match: null } : raw;
        if (value.match) return value.match.istatCode === place?.cityIstatCode ? SAME : { kind: "save", edit: cityEdit(value.match) };
        if (value.text.trim() === "") return place === null ? SAME : { kind: "save", edit: cityEdit(null) };
        return { kind: "invalid", message: t("leadCard.errors.city") };
      }}
      {...common(field, context)}
    />
  );
}

/**
 * Provincia, regione o nazione: si leggono dal luogo del Lead, con «dalla città», e non si modificano mai (nemmeno con
 * il permesso di modifica). La nazione c'è anche senza comune, per un Lead svizzero (questione aperta 120).
 */
function PlaceRow({ field, lead }: { field: CardField; lead: CardLead }) {
  const t = useT();
  const { locale } = useLocale();
  const { place } = lead;
  let value: string | null;
  if (field.ref === "system:province") value = place ? t("leadCard.data.provinceValue", { name: place.provinceName, abbreviation: place.provinceAbbreviation }) : null;
  else if (field.ref === "system:region") value = place?.regionName || null;
  else value = leadCountryName(locale, lead, t("cityPicker.italy"));
  const labelKey = SYSTEM_FIELD_LABELS[field.ref];
  return (
    <div className="g-lead-data__row">
      <dt>{labelKey ? t(labelKey) : field.ref}</dt>
      <dd className="g-lead-data__derived">
        <span>{value ?? "—"}</span>
        <span className="g-lead-data__from">{t("leadCard.data.fromCity")}</span>
      </dd>
    </div>
  );
}

/** Un campo dell'azienda: numero, euro, scelta o testo, con il nome che gli ha dato l'azienda. */
function CustomRow({ field, ...context }: RowContext & { field: CardField }) {
  const { locale } = useLocale();
  const t = useT();
  const key = field.ref.slice("field:".length);
  const raw = context.lead.custom[key] ?? null;
  const label = field.label ?? key;
  const props = { id: fieldId(field.ref), label, ...common(field, context) };

  if (field.type === "number" || field.type === "currency_eur") {
    const value = typeof raw === "number" ? raw : null;
    const display = value === null ? null : field.type === "currency_eur" ? formatEuro(locale, value) : formatNumber(locale, value);
    return (
      <EditableField
        {...props}
        display={display}
        spec={{ kind: field.type === "currency_eur" ? "currency" : "number" }}
        initial={value === null ? "" : formatEditableNumber(locale, value)}
        commit={(input) => {
          const next = parseDecimal(String(input), locale);
          if (next === "invalid") return { kind: "invalid", message: t("leadCard.errors.number") };
          return next === value ? SAME : { kind: "save", edit: customEdit(key, next) };
        }}
      />
    );
  }
  if (field.type === "choice") {
    const options = field.options ?? [];
    const value = typeof raw === "string" ? raw : null;
    // Una voce che il modello non offre più resta leggibile come il server la manda.
    const display = value === null ? null : (options.find((option) => option.id === value)?.label ?? value);
    return (
      <EditableField
        {...props}
        display={display}
        spec={{ kind: "choice", options }}
        initial={value ?? ""}
        commit={(input) => {
          const next = String(input) || null;
          return next === value ? SAME : { kind: "save", edit: customEdit(key, next) };
        }}
      />
    );
  }
  const value = typeof raw === "string" ? raw : raw === null ? null : String(raw);
  return (
    <EditableField
      {...props}
      display={value}
      spec={{ kind: "text", maxLength: TEXT_LIMITS.custom }}
      initial={value ?? ""}
      commit={(input) => {
        const next = String(input).trim() || null;
        return next === (value?.trim() || null) ? SAME : { kind: "save", edit: customEdit(key, next) };
      }}
    />
  );
}
